"use client";

import {
  AccountIcon,
  cn,
  Collapse,
  getAccountIconVariant,
  Icon,
  Select,
  SelectItem,
} from "@kyan/ui";
import { type ReactNode, useState } from "react";
import { FaCheck } from "react-icons/fa6";
import { Address } from "viem";

import { useAccountName } from "#/hooks/useAccountName";
import { useAccounts } from "#/hooks/useAccounts";
import { useAccount } from "#/store/wallet/account/account";
import { useEoa } from "#/store/wallet/eoa/eoa";

import { PortfolioRiskItems } from "./components/PortfolioRiskItems/PortfolioRiskItems";

const createAccountItems = (
  allAccounts: Address[],
  selectedAccount?: Address,
): {
  label: ReactNode;
  id: string;
}[] => {
  const items: { label: ReactNode; id: string }[] = [
    ...allAccounts.map((safe, index) => ({
      label: (
        <div className="flex items-center justify-between pr-5">
          <span className="flex w-full items-center gap-3 text-xs">
            <AccountIcon variant={getAccountIconVariant(index)} />
            {index === 0 ? "Main Account" : `Sub Account ${index}`}
          </span>
          {selectedAccount === safe && <FaCheck className="text-brandBlue" />}
        </div>
      ),
      id: safe,
    })),
  ];
  return items;
};

interface PortfolioAccountDropdownProps {
  className?: string;
}

export const PortfolioAccountDropdown: React.FC<
  PortfolioAccountDropdownProps
> = ({ className }) => {
  const { account } = useAccount();
  const { accounts } = useAccounts();
  const { isEoaConnected } = useEoa();
  const { accountName } = useAccountName();

  const [expandedAccount, setExpandedAccount] = useState(account);

  if (!isEoaConnected) return null;

  return (
    <Select
      value={account}
      defaultValue="0x123"
      triggerClassName={cn("border-divider h-12 w-63 pl-4", className)}
      contentClassName="animated-dropdown rounded-t-none border border-divider relative -top-1 bg-buttonHover dark:shadow-none shadow-[0px_5px_8px_rgba(201,201,201,0.50)]"
      valueComponent={
        <div className="flex items-center gap-3">
          {Boolean(account) && (
            <>
              <AccountIcon
                variant={getAccountIconVariant(
                  accounts.findIndex((address) => address === account),
                )}
              />

              <span>{accountName}</span>
            </>
          )}
          {!account && <span>No Account</span>}
        </div>
      }
    >
      {createAccountItems(accounts, account).map((accountItem) => (
        <SelectItem
          disabled
          key={accountItem.id}
          value={accountItem.id}
          className={cn(
            "dark:bg-buttonFill h-auto bg-white p-0 [&>span]:w-full",
          )}
        >
          {accountItem.id === "create" ? (
            <div className="flex h-[45px] items-center gap-3 px-3 text-xs">
              <Icon
                name="plus"
                className="text-primary flex size-5 items-center justify-center"
              />
              Create sub account
            </div>
          ) : (
            <Collapse
              iconClassName="text-textSecondary"
              isOpen={expandedAccount === accountItem.id}
              triggerElement={
                <div className="text-textPrimary w-full">
                  <span>{accountItem.label}</span>
                </div>
              }
              triggerClassName={cn(
                "dark:bg-buttonFill bg-buttonHover flex h-11 min-h-11 w-full cursor-pointer items-center justify-between border-b border-transparent bg-white px-4 transition-colors",
                {
                  "bg-buttonHover": accountItem.id === account,
                  "border-divider": expandedAccount === accountItem.id,
                },
              )}
              onIsOpenChange={(open) => {
                setExpandedAccount(
                  open ? (accountItem.id as Address) : undefined,
                );
              }}
            >
              <PortfolioRiskItems account={accountItem.id as Address} />
            </Collapse>
          )}
        </SelectItem>
      ))}
    </Select>
  );
};
